import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROLES = [
  { value: "teacher", label: "Enseignant(e)" },
  { value: "parent", label: "Parent" },
  { value: "member", label: "Membre" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "teacher" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé avec succès !");
      navigate("/mon-compte");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-[2rem] border border-slate-100 shadow-xl p-8 sm:p-10"
      >
        <div className="flex items-center gap-2 mb-6">
          <span className="grid place-items-center w-10 h-10 rounded-2xl bg-sky-500 text-white rotate-[-6deg]">
            <Sparkles className="w-5 h-5" />
          </span>
          <span className="font-heading font-extrabold text-2xl text-slate-900">Kwarizmix</span>
        </div>
        <h1 className="font-heading font-extrabold text-3xl text-slate-900">Créer un compte</h1>
        <p className="text-slate-500 mt-1 mb-8">Rejoignez l'aventure numérique.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label>Nom complet</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" data-testid="register-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1" data-testid="register-email" />
          </div>
          <div>
            <Label>Mot de passe</Label>
            <Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1" data-testid="register-password" />
          </div>
          <div>
            <Label>Je suis</Label>
            <div className="flex gap-2 mt-1">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setForm({ ...form, role: r.value })}
                  data-testid={`register-role-${r.value}`}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    form.role === r.value ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="w-full py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-[1.02] transition-[transform,background-color] disabled:opacity-60"
          >
            {loading ? "Création..." : "Créer mon compte"}
          </button>
        </form>
        <p className="text-center text-sm text-slate-500 mt-6">
          Déjà inscrit ?{" "}
          <Link to="/connexion" className="text-sky-600 font-bold" data-testid="link-login">
            Connectez-vous
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
